import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../hooks/useStore';
import { DataViewEditBox, Loading } from '../../../lib/components';
import { format } from 'date-fns';
import { FC } from 'react';
import { DataView } from './shared/view';
import { useCompanyBenefitSettingsData, usePaymentData } from '../../../queries/company';
import { Box } from '@mui/material';
import {
  BAVAdvisor,
  CompanyBenefitSettings,
  CompanyBKVContract
} from '@pxtra-client-lib-legacy/gen';

type Props = {
  dialogView?: boolean;
};

const formatDate = (date: string | null | undefined): string => {
  return date ? format(new Date(date), 'dd.MM.yyyy') : '';
};

const formatAmount = (amount: number | null | undefined): string => {
  return amount ? (amount / 100).toFixed(2) : '';
};

const formatContactName = (
  firstName?: string | null | undefined,
  lastName?: string | null | undefined
): string => {
  return [firstName, lastName].filter(Boolean).join(' ');
};

const buildSignedContractData = (contract: CompanyBKVContract) => {
  return {
    main_partner: contract?.partner ?? '',
    contract_number: contract?.contract_number ?? '',
    contract_start: formatDate(contract?.contract_start),
    contract_end: formatDate(contract?.contract_end),
    amount_employer: formatAmount(contract?.monthly_amount_employer),
    amount_employee: formatAmount(contract?.monthly_amount_employee),
    implementation_type:
      typeof contract?.implementation_type === 'string' ? contract.implementation_type : '',
    contact_name: formatContactName(contract?.advisor?.first_name, contract?.advisor?.last_name),
    contact_mail: contract?.advisor?.contact_mail ?? '',
    contact_number: contract?.advisor?.contact_number ?? ''
  };
};

const buildAdvisorContactData = (advisor: BAVAdvisor | undefined) => {
  return {
    contact_name: formatContactName(advisor?.first_name, advisor?.last_name),
    contact_mail: advisor?.contact_mail ?? '',
    contact_number: advisor?.contact_number ?? '',
    contact_link: advisor?.link ?? 'https://www.tpc-vorsorge.de/kontakt/'
  };
};

const getContractData = (
  benefitSettingsData: CompanyBenefitSettings | undefined,
  contractSigned: boolean
) => {
  if (!contractSigned || !benefitSettingsData) {
    return buildAdvisorContactData(benefitSettingsData?.bkv?.advisor);
  }

  const contracts = benefitSettingsData?.bkv?.contracts ?? [];
  const firstContract = contracts[0];

  return firstContract ? buildSignedContractData(firstContract) : {};
};

export const CompanyBKVSettings: FC<Props> = observer((props) => {
  const { t } = useTranslation();
  const { companyStore } = useStore();
  const { data: paymentData } = usePaymentData(companyStore.company?.id ?? '');
  const { data: benefitSettingsData, isLoading } = useCompanyBenefitSettingsData(
    companyStore.company?.id ?? ''
  );

  const settingsData = {
    enabled: paymentData?.booked_benefits?.includes('bkv')
  };

  const contractSigned = (benefitSettingsData?.bkv?.contracts?.length ?? 0) > 0;

  const contractData = getContractData(benefitSettingsData, contractSigned);

  const renderView = observer(() => (
    <DataView
      hideSettingData={props.dialogView}
      benefit="bkv"
      settingsData={settingsData}
      contractData={contractData}
      contractSigned={contractSigned}
    />
  ));

  const renderEdit = () => <></>;

  return isLoading ? (
    <Box className="w-[950px] mt-20">
      <Loading />
    </Box>
  ) : (
    <DataViewEditBox
      title={t('common:BKV')}
      view={renderView}
      edit={renderEdit}
      canEdit={false}
      canSubmit={false}
    />
  );
});
